import { AddAPhoto, AddCircleOutline, RemoveCircleOutline } from "@mui/icons-material"
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material"
import { StyledButton } from "components/Button/StyledButton"
import { CameraDialog } from "components/Camera/CameraDialog"
import { ImageSlider } from "components/ImageSlider/ImagesSlider"
import {
  DataType,
  DeviceAux,
  DeviceData,
  DeviceDataGroup,
  File,
  FileType,
  FileUploadInput,
  MovementData,
  MovementType,
  User,
} from "graphql/types"
import { useStoreActions } from "hooks/storeHooks"
import { enqueueSnackbar } from "notistack"
import { FormEvent, useEffect, useState } from "react"
import { emptyPerson } from "store/store"
import { getCurrentLocation } from "utils/getCurrentLocation"
import { useLocalStorage } from "utils/useLocalStorage"
import { v4 } from "uuid"

type AddAuxEquipmentDialogProps = {
  open: boolean
  onClose: () => void
  formState: DeviceAux
  onSubmit: (arg: DeviceAux) => void
}

type ValidationErrors = {
  name?: string
  barcode?: string
}

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`aux-tabpanel-${index}`}
      aria-labelledby={`aux-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  )
}

export const AddAuxEquipmentDialog = (props: AddAuxEquipmentDialogProps) => {
  const { open, onClose, formState, onSubmit } = props
  const { uploadFile } = useStoreActions((actions) => actions)

  const [localFormState, setLocalFormState] = useState<DeviceAux>(formState)
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({})
  const [tabValue, setTabValue] = useState(0)
  const [openCameraDialog, setOpenCameraDialog] = useState(false)
  const [openMovementDialog, setOpenMovementDialog] = useState(false)
  const [user] = useLocalStorage<User>("user", emptyPerson)

  useEffect(() => {
    setLocalFormState({
      ...formState,
      movementData: formState.movementData || [],
      deviceDataGroup: formState.deviceDataGroup || [],
      images: formState.images || [],
    })
    setValidationErrors({}) // Clear errors when form state changes
    setTabValue(0) // Reset to first tab
  }, [formState])

  const validateForm = (): ValidationErrors => {
    const errors: ValidationErrors = {}

    // Check required fields
    if (!localFormState.name?.trim()) {
      errors.name = "Bezeichnung ist erforderlich"
    }

    if (!localFormState.barcode?.trim()) {
      errors.barcode = "Barcode ist erforderlich"
    }

    return errors
  }

  const isFormValid = (): boolean => {
    const errors = validateForm()
    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleFormChange = (
    e:
      | React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>
      | SelectChangeEvent<string>
  ) => {
    const { name, value } = e.target
    if (name) {
      setLocalFormState((prevState) => ({ ...prevState, [name]: value }))

      // Clear validation error for this field
      if (validationErrors[name as keyof ValidationErrors]) {
        setValidationErrors((prev) => {
          const newErrors = { ...prev }
          delete newErrors[name as keyof ValidationErrors]
          return newErrors
        })
      }
    }
  }

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue)
  }

  // Movement Data handlers
  const handleAddMovement = (movement: MovementData) => {
    setLocalFormState({
      ...localFormState,
      movementData: [...localFormState.movementData, movement],
    })
  }

  const handleDeleteMovement = (index: number) => {
    setLocalFormState({
      ...localFormState,
      movementData: localFormState.movementData.filter((_, i) => i !== index),
    })
  }

  // Device Data Group handlers
  const handleAddDataGroup = () => {
    const newGroup: DeviceDataGroup = {
      id: v4(),
      name: "Neue Gruppe",
      deviceData: [],
      isAux: true,
      isDefault: false,
    }
    setLocalFormState({
      ...localFormState,
      deviceDataGroup: [...localFormState.deviceDataGroup, newGroup],
    })
  }

  const handleUpdateDataGroup = (index: number, group: DeviceDataGroup) => {
    const newGroups = [...localFormState.deviceDataGroup]
    newGroups[index] = group
    setLocalFormState({
      ...localFormState,
      deviceDataGroup: newGroups,
    })
  }

  const handleDeleteDataGroup = (index: number) => {
    setLocalFormState({
      ...localFormState,
      deviceDataGroup: localFormState.deviceDataGroup.filter((_, i) => i !== index),
    })
  }

  const handleAddDataField = (groupIndex: number) => {
    const newField: DeviceData = {
      id: v4(),
      name: "Neues Feld",
      dataType: DataType.STRING,
      unit: [],
      isArray: false,
      data: [],
      isCalculation: false,
      isOperatingMode: false,
    }
    const newGroups = [...localFormState.deviceDataGroup]
    newGroups[groupIndex].deviceData.push(newField)
    setLocalFormState({
      ...localFormState,
      deviceDataGroup: newGroups,
    })
  }

  const handleUpdateDataField = (groupIndex: number, fieldIndex: number, field: DeviceData) => {
    const newGroups = [...localFormState.deviceDataGroup]
    newGroups[groupIndex].deviceData[fieldIndex] = field
    setLocalFormState({
      ...localFormState,
      deviceDataGroup: newGroups,
    })
  }

  const handleDeleteDataField = (groupIndex: number, fieldIndex: number) => {
    const newGroups = [...localFormState.deviceDataGroup]
    newGroups[groupIndex].deviceData = newGroups[groupIndex].deviceData.filter(
      (_, i) => i !== fieldIndex
    )
    setLocalFormState({
      ...localFormState,
      deviceDataGroup: newGroups,
    })
  }

  // Image handlers
  const handleAddImage = async (event: FormEvent<HTMLFormElement>, fotos: string[]) => {
    event.preventDefault()
    if (!fotos.length) {
      enqueueSnackbar("Kein Bild erfasst!", { variant: "error" })
      return
    }

    const formData = new FormData(event.currentTarget)

    const uploadImage: FileUploadInput = {
      id: "NEW",
      filename: v4() + ".png",
      name: formData.get("title") as string,
      base64: fotos[0],
      type: FileType.IMAGE,
      createdAt: new Date().toISOString(),
      createdBy: user.id,
    }

    const imageID = await uploadFile(uploadImage)

    const newImage: File = {
      id: imageID,
      createdAt: uploadImage.createdAt,
      createdBy: uploadImage.createdBy,
      fileURL: "",
      name: uploadImage.name,
      type: uploadImage.type,
      filename: uploadImage.filename,
    }

    setLocalFormState({
      ...localFormState,
      images: [...localFormState.images, newImage],
    })
  }

  const handleDeleteImage = (image: File) => {
    setLocalFormState({
      ...localFormState,
      images: localFormState.images.filter((item) => item.id !== image.id),
    })
  }

  const handleSave = () => {
    if (isFormValid()) {
      onSubmit(localFormState)
    }
  }

  const handleClose = () => {
    setLocalFormState(formState)
    setValidationErrors({})
    setTabValue(0)
    onClose()
  }

  const hasValidationErrors = Object.keys(validationErrors).length > 0
  const currentLocation = getCurrentLocation(localFormState.movementData || [])

  return (
    <Dialog open={open} maxWidth="lg" fullWidth>
      <DialogTitle>{localFormState.id !== "NEW" ? "AUX bearbeiten" : "AUX hinzufügen"}</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {hasValidationErrors && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1 }}>
              Bitte korrigieren Sie die folgenden Fehler:
            </Typography>
            <Stack spacing={0.5}>
              {Object.entries(validationErrors).map(([field, error]) => (
                <Typography key={field} variant="body2">
                  • {error}
                </Typography>
              ))}
            </Stack>
          </Alert>
        )}

        <Tabs value={tabValue} onChange={handleTabChange} aria-label="AUX Tabs">
          <Tab label="Basisdaten" />
          <Tab label="Bewegungsdaten" />
          <Tab label="Spezifikationen" />
          <Tab label="Bilder" />
        </Tabs>

        {/* Tab 1: Basic Information */}
        <TabPanel value={tabValue} index={0}>
          <Stack spacing={2}>
            <TextField
              size="small"
              name="name"
              label="Bezeichnung"
              type="text"
              fullWidth
              value={localFormState.name}
              onChange={handleFormChange}
              required
              error={!!validationErrors.name}
              helperText={validationErrors.name}
            />

            <TextField
              size="small"
              name="barcode"
              label="Barcode"
              type="text"
              fullWidth
              value={localFormState.barcode}
              onChange={handleFormChange}
              required
              error={!!validationErrors.barcode}
              helperText={validationErrors.barcode}
              placeholder="AUX-2024-001"
            />

            <TextField
              size="small"
              name="description"
              label="Beschreibung"
              type="text"
              fullWidth
              multiline
              rows={3}
              value={localFormState.description}
              onChange={handleFormChange}
              placeholder="Beschreibung des AUX und seiner Verwendung"
            />
          </Stack>
        </TabPanel>

        {/* Tab 2: Movement Data */}
        <TabPanel value={tabValue} index={1}>
          <Stack spacing={2}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="subtitle1">
                Bewegungsdaten {currentLocation && `(Aktueller Ort: ${currentLocation})`}
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddCircleOutline />}
                onClick={() => setOpenMovementDialog(true)}
                size="small"
              >
                Bewegung hinzufügen
              </Button>
            </Box>

            {localFormState.movementData.length > 0 ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {localFormState.movementData.map((movement, index) => (
                  <Box
                    key={index}
                    sx={{
                      p: 2,
                      border: "1px solid #e0e0e0",
                      borderRadius: 1,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "start",
                    }}
                  >
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {movement.type === MovementType.ENTRANCE
                          ? "Eingang"
                          : movement.type === MovementType.EXIT
                            ? "Ausgang"
                            : "Umlagerung"}
                      </Typography>
                      <Typography variant="body2">
                        Datum: {new Date(movement.date).toLocaleDateString("de-DE")}
                      </Typography>
                      {movement.location && (
                        <Typography variant="body2">Ort: {movement.location}</Typography>
                      )}
                      {movement.shipmentNumber && (
                        <Typography variant="body2">
                          Sendungsnr.: {movement.shipmentNumber}
                        </Typography>
                      )}
                      {movement.carrier && (
                        <Typography variant="body2">Spedition: {movement.carrier}</Typography>
                      )}
                      {movement.note && (
                        <Typography variant="body2">Notiz: {movement.note}</Typography>
                      )}
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteMovement(index)}
                      color="error"
                    >
                      <RemoveCircleOutline />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Keine Bewegungsdaten vorhanden
              </Typography>
            )}
          </Stack>
        </TabPanel>

        {/* Tab 3: Device Data Groups */}
        <TabPanel value={tabValue} index={2}>
          <Stack spacing={2}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="subtitle1">Gerätespezifikationen</Typography>
              <Button
                variant="contained"
                startIcon={<AddCircleOutline />}
                onClick={handleAddDataGroup}
                size="small"
              >
                Gruppe hinzufügen
              </Button>
            </Box>

            {localFormState.deviceDataGroup.length > 0 ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {localFormState.deviceDataGroup.map((group, groupIndex) => (
                  <Box
                    key={groupIndex}
                    sx={{
                      p: 2,
                      border: "1px solid #e0e0e0",
                      borderRadius: 1,
                    }}
                  >
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                      <TextField
                        size="small"
                        label="Gruppenname"
                        value={group.name}
                        onChange={(e) =>
                          handleUpdateDataGroup(groupIndex, { ...group, name: e.target.value })
                        }
                        sx={{ flexGrow: 1, mr: 2 }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteDataGroup(groupIndex)}
                        color="error"
                      >
                        <RemoveCircleOutline />
                      </IconButton>
                    </Box>

                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                      {group.deviceData.map((field, fieldIndex) => (
                        <Box
                          key={fieldIndex}
                          sx={{ display: "flex", gap: 1, alignItems: "center" }}
                        >
                          <TextField
                            size="small"
                            label="Feldname"
                            value={field.name}
                            onChange={(e) =>
                              handleUpdateDataField(groupIndex, fieldIndex, {
                                ...field,
                                name: e.target.value,
                              })
                            }
                            sx={{ flex: 2 }}
                          />
                          <FormControl size="small" sx={{ flex: 1 }}>
                            <InputLabel>Typ</InputLabel>
                            <Select
                              value={field.dataType}
                              label="Typ"
                              onChange={(e) =>
                                handleUpdateDataField(groupIndex, fieldIndex, {
                                  ...field,
                                  dataType: e.target.value as DataType,
                                })
                              }
                            >
                              <MenuItem value={DataType.STRING}>Text</MenuItem>
                              <MenuItem value={DataType.FLOAT}>Zahl</MenuItem>
                              <MenuItem value={DataType.BOOLEAN}>Ja/Nein</MenuItem>
                            </Select>
                          </FormControl>
                          <TextField
                            size="small"
                            label="Einheit"
                            value={field.unit.join(", ")}
                            onChange={(e) =>
                              handleUpdateDataField(groupIndex, fieldIndex, {
                                ...field,
                                unit: e.target.value
                                  .split(",")
                                  .map((u) => u.trim())
                                  .filter(Boolean),
                              })
                            }
                            sx={{ flex: 1 }}
                            placeholder="V, A, °C"
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={field.isArray}
                                onChange={(e) =>
                                  handleUpdateDataField(groupIndex, fieldIndex, {
                                    ...field,
                                    isArray: e.target.checked,
                                  })
                                }
                              />
                            }
                            label="Mehrfach"
                          />
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteDataField(groupIndex, fieldIndex)}
                            color="error"
                          >
                            <RemoveCircleOutline />
                          </IconButton>
                        </Box>
                      ))}
                      <Button
                        variant="outlined"
                        startIcon={<AddCircleOutline />}
                        onClick={() => handleAddDataField(groupIndex)}
                        size="small"
                      >
                        Feld hinzufügen
                      </Button>
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Keine Spezifikationsgruppen vorhanden
              </Typography>
            )}
          </Stack>
        </TabPanel>

        {/* Tab 4: Images */}
        <TabPanel value={tabValue} index={3}>
          <Stack spacing={2}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="subtitle1">Bilder ({localFormState.images.length})</Typography>
              <Button
                variant="contained"
                startIcon={<AddAPhoto />}
                onClick={() => setOpenCameraDialog(true)}
                size="small"
              >
                Bild hinzufügen
              </Button>
            </Box>

            {localFormState.images.length > 0 ? (
              <ImageSlider images={localFormState.images} onDelete={handleDeleteImage} />
            ) : (
              <Typography variant="body2" color="text.secondary">
                Keine Bilder vorhanden
              </Typography>
            )}
          </Stack>
        </TabPanel>

        {/* Movement Dialog */}
        <MovementDialog
          open={openMovementDialog}
          onClose={() => setOpenMovementDialog(false)}
          onSave={handleAddMovement}
          barcode={localFormState.barcode}
        />

        {/* Camera Dialog */}
        <CameraDialog
          open={openCameraDialog}
          onClose={() => setOpenCameraDialog(false)}
          onSave={handleAddImage}
          imageName={`AUX: ${localFormState.name || localFormState.barcode}`}
        />
      </DialogContent>
      <DialogActions>
        <StyledButton buttonType="cancel" onClick={handleClose} />
        <StyledButton buttonType="save" onClick={handleSave} disabled={hasValidationErrors} />
      </DialogActions>
    </Dialog>
  )
}

// Movement Dialog Component
interface MovementDialogProps {
  open: boolean
  onClose: () => void
  onSave: (movement: MovementData) => void
  barcode: string
}

const MovementDialog = ({ open, onClose, onSave, barcode }: MovementDialogProps) => {
  const [movementData, setMovementData] = useState<MovementData>({
    type: MovementType.ENTRANCE,
    date: new Date().toISOString(),
    shipmentNumber: "",
    carrier: "",
    location: "",
    note: "",
    eutIsOk: true,
    packageIsOk: true,
  })

  const handleSave = () => {
    onSave(movementData)
    // Reset form
    setMovementData({
      type: MovementType.ENTRANCE,
      date: new Date().toISOString(),
      shipmentNumber: "",
      carrier: "",
      location: "",
      note: "",
      eutIsOk: true,
      packageIsOk: true,
    })
    onClose()
  }

  const isShown = movementData.type !== MovementType.IN_HOUSE

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Bewegung erfassen für {barcode}</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <FormControl size="small" fullWidth sx={{ mt: 1 }}>
          <InputLabel>Bewegungstyp</InputLabel>
          <Select
            label="Bewegungstyp"
            value={movementData.type}
            onChange={(e) =>
              setMovementData({
                ...movementData,
                type: e.target.value as MovementType,
              })
            }
          >
            <MenuItem value={MovementType.ENTRANCE}>Eingang</MenuItem>
            <MenuItem value={MovementType.IN_HOUSE}>Umlagerung</MenuItem>
            <MenuItem value={MovementType.EXIT}>Ausgang</MenuItem>
          </Select>
        </FormControl>

        {isShown && (
          <TextField
            size="small"
            label="Sendungsnummer"
            value={movementData.shipmentNumber}
            onChange={(e) =>
              setMovementData({
                ...movementData,
                shipmentNumber: e.target.value,
              })
            }
            fullWidth
          />
        )}

        {isShown && (
          <TextField
            size="small"
            label="Spedition"
            value={movementData.carrier}
            onChange={(e) =>
              setMovementData({
                ...movementData,
                carrier: e.target.value,
              })
            }
            fullWidth
          />
        )}

        <TextField
          size="small"
          label="Lagerplatz"
          value={movementData.location}
          onChange={(e) =>
            setMovementData({
              ...movementData,
              location: e.target.value,
            })
          }
          fullWidth
          required
        />

        {isShown && (
          <>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!movementData.packageIsOk}
                  onChange={(e) =>
                    setMovementData({
                      ...movementData,
                      packageIsOk: !e.target.checked,
                    })
                  }
                />
              }
              label="Verpackung ist beschädigt"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={!movementData.eutIsOk}
                  onChange={(e) =>
                    setMovementData({
                      ...movementData,
                      eutIsOk: !e.target.checked,
                    })
                  }
                />
              }
              label="Gerät ist augenscheinlich beschädigt"
            />
          </>
        )}

        <TextField
          size="small"
          label="Notiz / Anmerkung"
          value={movementData.note}
          onChange={(e) =>
            setMovementData({
              ...movementData,
              note: e.target.value,
            })
          }
          multiline
          rows={2}
          fullWidth
        />
      </DialogContent>
      <DialogActions>
        <StyledButton buttonType="cancel" onClick={onClose} />
        <StyledButton buttonType="save" onClick={handleSave} />
      </DialogActions>
    </Dialog>
  )
}
